import React, { useState } from 'react';
import { ShareNetwork, DownloadSimple, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * Universal Save / Share button.
 * - Mobile: opens the NATIVE iOS / Android share sheet with the file attached
 *   (Photos, Instagram, WhatsApp, AirDrop, Messages, Mail, etc.)
 * - Desktop / unsupported: falls back to a clean direct download
 *
 * Props:
 *   url: full or relative URL of the file
 *   filename: suggested filename when downloading / sharing
 *   title: title used in native share sheet
 *   text: caption used in native share sheet
 *   mimeType: optional mime to share as a File object
 *   variant: 'pill' | 'compact'
 */
export default function ShareSaveButton({
  url, filename, title = 'Share', text = '', mimeType, variant = 'pill', dataTestId = 'share-save-btn',
}) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const fullUrl = url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

  const guessMime = (name = '') => {
    const ext = name.toLowerCase().split('.').pop();
    return ({
      png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif',
      mp4: 'video/mp4', mov: 'video/quicktime', pdf: 'application/pdf', mp3: 'audio/mpeg',
      zip: 'application/zip', html: 'text/html',
    })[ext] || mimeType || 'application/octet-stream';
  };

  // Fetch the asset as a File so the native share sheet treats it properly
  const fetchAsFile = async () => {
    const resp = await fetch(fullUrl, { credentials: 'omit', cache: 'no-cache' });
    if (!resp.ok) throw new Error(`Fetch failed: ${resp.status}`);
    const blob = await resp.blob();
    const name = filename || (fullUrl.split('/').pop() || 'asset');
    const type = blob.type || guessMime(name);
    return new File([blob], name, { type });
  };

  const handleShare = async () => {
    if (!fullUrl || busy) return;
    setBusy(true);
    try {
      // 1. Native share sheet WITH the file (best UX — full native options)
      if (typeof navigator !== 'undefined' && navigator.share && navigator.canShare) {
        try {
          const file = await fetchAsFile();
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title, text });
            return;
          }
        } catch (innerErr) {
          if (innerErr?.name === 'AbortError') return; // user cancelled
          // fall through
        }
      }

      // 2. URL-only native share (Android Chrome can't always share files but can share URLs)
      if (typeof navigator !== 'undefined' && navigator.share) {
        try {
          await navigator.share({ title, text, url: fullUrl });
          return;
        } catch (urlErr) {
          if (urlErr?.name === 'AbortError') return;
          // fall through
        }
      }

      // 3. Last resort — copy URL
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success('Link copied — paste anywhere to share');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not share — try Save instead');
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    if (!fullUrl || busy) return;
    setBusy(true);
    try {
      // Mobile Safari (iOS): Web Share with file is the native way to "save" to Photos / Files
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || '') && !window.MSStream;
      if (isIOS && navigator.share && navigator.canShare) {
        try {
          const file = await fetchAsFile();
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: filename || title });
            return;
          }
        } catch (e) {
          if (e?.name === 'AbortError') return;
        }
      }

      // Desktop / Android — direct download via blob
      const resp = await fetch(fullUrl, { credentials: 'omit', cache: 'no-cache' });
      const blob = await resp.blob();
      const objUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = filename || (fullUrl.split('/').pop() || 'asset');
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objUrl);
      toast.success('Saved to your device');
    } catch {
      // fallback: open in new tab so user can long-press / save
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setBusy(false);
    }
  };

  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-1.5">
        <button onClick={handleShare} disabled={busy} data-testid={`${dataTestId}-share`}
          className="p-2 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50" title="Share">
          {copied ? <Check size={14} weight="bold" style={{ color: '#22c55e' }} /> : <ShareNetwork size={14} style={{ color: '#c8f060' }} />}
        </button>
        <button onClick={handleDownload} disabled={busy} data-testid={`${dataTestId}-download`}
          className="p-2 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50" title="Save">
          <DownloadSimple size={14} style={{ color: 'rgba(245,240,232,0.6)' }} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <button onClick={handleShare} disabled={busy} data-testid={`${dataTestId}-share`}
        className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all disabled:opacity-50"
        style={{ background: 'rgba(200,240,96,0.08)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.2)' }}>
        {copied ? <Check size={12} weight="bold" /> : <ShareNetwork size={12} weight="bold" />}
        {copied ? 'Copied' : 'Share'}
      </button>
      <button onClick={handleDownload} disabled={busy} data-testid={`${dataTestId}-download`}
        className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all disabled:opacity-50"
        style={{ background: '#c8f060', color: '#080808' }}>
        <DownloadSimple size={12} weight="bold" /> Save
      </button>
    </div>
  );
}
